<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import PlanCard from './components/PlanCard.vue';
import PaymentMethodSelector from './components/PaymentMethodSelector.vue';
import PaymentPending from './components/PaymentPending.vue';
import ButtonV4 from 'next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

const billingCycle = ref('monthly');
const selectedPlan = ref(null);
const showPaymentModal = ref(false);
const selectedPaymentMethod = ref(null);

const plans = useMapGetter('billing/getPlans');
const subscription = useMapGetter('billing/getSubscription');
const supportInfo = useMapGetter('billing/getSupportInfo');
const paymentMethods = useMapGetter('billing/getPaymentMethods');
const pendingPayment = useMapGetter('billing/getPendingPayment');
const uiFlags = useMapGetter('billing/getUIFlags');

const currentPlanId = computed(() => subscription.value?.plan?.id);
const supportWhatsApp = computed(() => supportInfo.value?.whatsapp || '');
const supportEmail = computed(() => supportInfo.value?.email || '');
const hasSupportContact = computed(
  () => !!supportWhatsApp.value || !!supportEmail.value
);
const showPendingPayment = computed(() => !!pendingPayment.value);

const onContactSupport = () => {
  const whatsapp = supportInfo.value?.whatsapp;
  const email = supportInfo.value?.email;

  // Priority: WhatsApp > Chatwoot Widget > Email
  if (whatsapp) {
    const cleanNumber = whatsapp.replace(/\D/g, '');
    const message = encodeURIComponent(
      'Ola! Preciso de ajuda com um plano personalizado.'
    );
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, '_blank');
  } else if (window.$chatwoot) {
    window.$chatwoot.toggle();
  } else if (email) {
    window.location.href = `mailto:${email}?subject=Plano Personalizado`;
  }
};

const yearlyDiscount = computed(() => {
  // Calculate average discount across all plans
  const plansWithDiscount = plans.value.filter(
    p => p.monthly_price > 0 && p.yearly_price > 0
  );
  if (plansWithDiscount.length === 0) return 0;

  const totalDiscount = plansWithDiscount.reduce((acc, plan) => {
    const monthlyAnnual = plan.monthly_price * 12;
    return acc + ((monthlyAnnual - plan.yearly_price) / monthlyAnnual) * 100;
  }, 0);

  return Math.round(totalDiscount / plansWithDiscount.length);
});

const toggleBillingCycle = cycle => {
  billingCycle.value = cycle;
};

const handleSelectPlan = async plan => {
  selectedPlan.value = plan;

  // If plan is free, just change it
  if (!plan.monthly_price || plan.monthly_price === 0) {
    const success = await store.dispatch('billing/changePlan', {
      planId: plan.id,
      billingCycle: billingCycle.value,
    });
    if (success) {
      router.push({
        name: 'billing_settings_index',
        params: { accountId: accountId.value },
      });
    }
    return;
  }

  // Ensure payment methods are loaded
  if (paymentMethods.value.length === 0) {
    await store.dispatch('billing/fetchPaymentMethods');
  }

  // If multiple payment methods are available, show selector
  if (paymentMethods.value.length > 1) {
    selectedPaymentMethod.value = paymentMethods.value[0]?.id || null;
    showPaymentModal.value = true;
    return;
  }

  // Otherwise, proceed with default payment method
  await processCheckout(paymentMethods.value[0]?.id || 'stripe');
};

const handlePaymentMethodSelect = async methodId => {
  selectedPaymentMethod.value = methodId;
  showPaymentModal.value = false;
  await processCheckout(methodId);
};

const processCheckout = async paymentMethod => {
  const result = await store.dispatch('billing/checkout', {
    planId: selectedPlan.value.id,
    billingCycle: billingCycle.value,
    paymentMethod,
  });

  // If PIX or Boleto, the pendingPayment will be set and modal will show automatically
  if (result.type === 'redirect') {
    // Will redirect to external checkout
  }
};

const closePendingPayment = () => {
  store.dispatch('billing/clearPendingPayment');
};

const refreshPaymentStatus = async () => {
  // Refresh billing info to check if payment was confirmed
  await store.dispatch('billing/fetchBillingInfo');

  // If subscription is now active, close the modal and redirect
  if (subscription.value?.status === 'active') {
    store.dispatch('billing/clearPendingPayment');
    router.push({
      name: 'billing_settings_index',
      params: { accountId: accountId.value },
    });
  }
};

const goBack = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

onMounted(() => {
  store.dispatch('billing/fetchPlans');
  store.dispatch('billing/fetchBillingInfo');
  store.dispatch('billing/fetchSupportInfo');
  store.dispatch('billing/fetchPaymentMethods');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingPlans"
    :loading-message="$t('BILLING_SETTINGS.LOADING')"
    :no-records-found="plans.length === 0"
    :no-records-message="$t('BILLING_SETTINGS.PLANS.NO_PLANS')"
  >
    <template #header>
      <div class="flex items-center gap-4 mb-4">
        <button
          class="p-2 rounded-lg hover:bg-n-slate-3 transition-colors"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left text-lg text-n-slate-11" />
        </button>
        <div>
          <h1 class="text-xl font-bold text-n-slate-12">
            {{ $t('BILLING_SETTINGS.PLANS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-10">
            {{ $t('BILLING_SETTINGS.PLANS.DESCRIPTION') }}
          </p>
        </div>
      </div>
    </template>

    <template #body>
      <section class="space-y-8">
        <!-- Billing cycle toggle -->
        <div class="flex justify-center">
          <div class="inline-flex items-center p-1 rounded-xl bg-n-slate-3">
            <button
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              :class="[
                billingCycle === 'monthly'
                  ? 'bg-white dark:bg-n-slate-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12',
              ]"
              @click="toggleBillingCycle('monthly')"
            >
              {{ $t('BILLING_SETTINGS.PLANS.MONTHLY') }}
            </button>
            <button
              class="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
              :class="[
                billingCycle === 'yearly'
                  ? 'bg-white dark:bg-n-slate-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12',
              ]"
              @click="toggleBillingCycle('yearly')"
            >
              {{ $t('BILLING_SETTINGS.PLANS.YEARLY') }}
              <span
                v-if="yearlyDiscount > 0"
                class="px-2 py-0.5 rounded-full bg-n-teal-9 text-white text-xs"
              >
                -{{ yearlyDiscount }}%
              </span>
            </button>
          </div>
        </div>

        <!-- Plans grid -->
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch"
        >
          <PlanCard
            v-for="plan in plans"
            :key="plan.id"
            :plan="plan"
            :billing-cycle="billingCycle"
            :is-current-plan="plan.id === currentPlanId"
            :is-loading="
              uiFlags.isCheckoutInProcess && selectedPlan?.id === plan.id
            "
            @select="handleSelectPlan"
          />
        </div>

        <!-- Footer info -->
        <div class="text-center text-sm text-n-slate-10 space-y-2">
          <p>{{ $t('BILLING_SETTINGS.PLANS.ALL_PLANS_INCLUDE') }}</p>
          <p v-if="hasSupportContact">
            {{ $t('BILLING_SETTINGS.PLANS.NEED_CUSTOM') }}
            <button
              class="text-woot-500 hover:underline"
              @click="onContactSupport"
            >
              {{ $t('BILLING_SETTINGS.PLANS.CONTACT_US') }}
            </button>
          </p>
        </div>
      </section>

      <!-- Payment Method Selection Modal -->
      <Modal
        :show="showPaymentModal"
        size="small"
        @close="showPaymentModal = false"
      >
        <div class="p-6">
          <PaymentMethodSelector
            :payment-methods="paymentMethods"
            :selected-method="selectedPaymentMethod"
            :is-loading="uiFlags.isCheckoutInProcess"
            @select="handlePaymentMethodSelect"
            @cancel="showPaymentModal = false"
          />
        </div>
      </Modal>

      <!-- Pending Payment Modal (PIX/Boleto) -->
      <Modal
        :show="showPendingPayment"
        size="small"
        @close="closePendingPayment"
      >
        <div class="p-6">
          <PaymentPending
            v-if="pendingPayment"
            :payment="pendingPayment"
            @close="closePendingPayment"
            @refresh="refreshPaymentStatus"
          />
        </div>
      </Modal>
    </template>
  </SettingsLayout>
</template>
